import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { ChevronRightIcon, CalendarDaysIcon } from "@heroicons/react/20/solid";
import { useVenue } from "../VenueProvider";
import { ACCENT, formatMXN, Section, CustomTooltip } from "../cuentas/shared";
import { formatCalendarDate } from "../../utils/dates";
import { agendaVendida } from "../../utils/reportes";
import { Kpi, labelMes, fmtEje, AGENDA_COBRADO, AGENDA_SALDO } from "./shared";

// Lo que ya está vendido y todavía no ocurre, mes por mes hacia adelante.
// Contesta la pregunta con la que se abre el año: "¿cuánto traigo cerrado y
// para cuándo?". Antes había que ir al Estado de resultados, cambiarle el
// año a mano y leer los renglones "por venir" de dos tablas distintas.
//
// Deliberadamente SIN utilidad ni costos: los costos de un evento que no ha
// pasado no están capturados y la utilidad saldría inflada. Lo que sí se
// puede afirmar de un evento contratado es cuánto vale y cuánto ya entró —
// ver la nota en `agendaVendida`.
const ReporteAgendaVendida = () => {
  const navigate = useNavigate();
  const { events, calendarTimezone: tz } = useVenue();

  const agenda = useMemo(
    () => agendaVendida({ events, timeZone: tz }),
    [events, tz]
  );
  const { meses, totales, ultimoMes, mesesConEventos, truncada } = agenda;

  const datos = useMemo(
    () => meses.map((m) => ({ ...m, label: labelMes(m.key) })),
    [meses]
  );

  const [mesAbierto, setMesAbierto] = useState(null);
  const detalle = useMemo(
    () => (mesAbierto ? agenda.eventos.filter((e) => e.mes === mesAbierto) : []),
    [mesAbierto, agenda]
  );

  const pctCobrado = totales.valor > 0 ? totales.cobrado / totales.valor : null;
  const mejorMes = useMemo(
    () => meses.reduce((a, b) => (b.valor > (a?.valor ?? -1) ? b : a), null),
    [meses]
  );

  if (meses.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-10 text-center shadow-sm">
        <CalendarDaysIcon className="h-8 w-8 mx-auto text-zinc-300" />
        <p className="mt-2 text-sm text-zinc-400">
          No tienes eventos contratados por venir. En cuanto agendes uno con
          fecha futura, aquí verás cuánto vale y para qué mes es.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi
          label="Contratado por venir"
          value={formatMXN(totales.valor)}
          hint={`${totales.eventos} ${totales.eventos === 1 ? "evento" : "eventos"} por delante`}
        />
        <Kpi
          label="Ya cobrado"
          value={formatMXN(totales.cobrado)}
          tone="text-emerald-600"
          hint={
            pctCobrado !== null
              ? `${Math.round(pctCobrado * 100)}% de lo contratado`
              : "Sin valor capturado"
          }
        />
        <Kpi
          label="Por cobrar"
          value={formatMXN(totales.saldo)}
          tone={totales.saldo > 0 ? "text-amber-600" : ""}
          hint="Saldo de esos mismos eventos"
        />
        <Kpi
          label="Agenda hasta"
          value={ultimoMes ? labelMes(ultimoMes) : "—"}
          hint={`${mesesConEventos} ${mesesConEventos === 1 ? "mes" : "meses"} con eventos vendidos`}
        />
      </div>

      <Section
        icon={CalendarDaysIcon}
        title="Lo vendido, mes por mes"
        action={
          mejorMes && mejorMes.valor > 0 ? (
            <span className="text-xs text-zinc-400">
              Mes más vendido: {labelMes(mejorMes.key)}
            </span>
          ) : null
        }
      >
        {/* Barras apiladas: cobrado + por cobrar sí suman el contratado del
            mes, así que apilar dice la verdad. Un `stackId` con dos pasos de
            un mismo tono (no dos colores) porque es un total en dos estados,
            no dos categorías. */}
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datos} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={fmtEje}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="cobrado"
                name="Ya cobrado"
                stackId="a"
                fill={AGENDA_COBRADO}
                /* 2px de aire entre los dos tramos: sin el gap la frontera
                   entre pasos del mismo tono se pierde. */
                stroke="#ffffff"
                strokeWidth={2}
              />
              <Bar
                dataKey="saldo"
                name="Por cobrar"
                stackId="a"
                fill={AGENDA_SALDO}
                stroke="#ffffff"
                strokeWidth={2}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {truncada && (
          <p className="mt-3 text-xs text-zinc-400">
            La gráfica llega hasta {labelMes(datos[datos.length - 1].key)}; tienes
            eventos vendidos hasta {labelMes(ultimoMes)}.
          </p>
        )}
      </Section>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-800/40">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Mes</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Eventos</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Contratado</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Ya cobrado</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Por cobrar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {datos.map((m) => {
                const abierto = mesAbierto === m.key;
                const vacio = m.eventos === 0;
                return (
                  <React.Fragment key={m.key}>
                    <tr
                      onClick={() => !vacio && setMesAbierto(abierto ? null : m.key)}
                      className={`transition ${
                        vacio
                          ? "text-zinc-300 dark:text-zinc-600"
                          : "cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                      }`}
                      title={vacio ? "Sin eventos vendidos este mes" : "Ver los eventos del mes"}
                    >
                      <td className="px-4 py-2 font-medium whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <ChevronRightIcon
                            className={`h-4 w-4 transition-transform ${
                              vacio ? "opacity-0" : "text-zinc-400"
                            } ${abierto ? "rotate-90" : ""}`}
                          />
                          <span className={vacio ? "" : "text-zinc-900 dark:text-white"}>
                            {m.label}
                          </span>
                          {m.parcial && (
                            <span
                              className="ml-1 text-[10px] uppercase tracking-wide text-zinc-400"
                              title="Solo lo que queda del mes, de hoy en adelante"
                            >
                              lo que queda
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-zinc-500">
                        {vacio ? "—" : m.eventos}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {vacio ? "—" : formatMXN(m.valor)}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {m.cobrado > 0 ? (
                          <span className="text-emerald-600">{formatMXN(m.cobrado)}</span>
                        ) : (
                          <span className="text-zinc-300 dark:text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {m.saldo > 0 ? (
                          <span className="text-amber-600">{formatMXN(m.saldo)}</span>
                        ) : (
                          <span className="text-zinc-300 dark:text-zinc-600">—</span>
                        )}
                      </td>
                    </tr>
                    {abierto && (
                      <tr>
                        <td colSpan={5} className="px-5 py-4 bg-zinc-50/70 dark:bg-zinc-800/30">
                          <div className="space-y-1">
                            <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-zinc-400">
                              <span className="w-24 shrink-0">Fecha</span>
                              <span>Evento</span>
                              <span className="ml-auto w-28 text-right">Contratado</span>
                              <span className="w-28 text-right">Cobrado</span>
                              <span className="w-28 text-right">Por cobrar</span>
                            </div>
                            {detalle.map((e) => (
                              <div key={e.id} className="flex items-baseline gap-2 text-xs">
                                <span className="text-zinc-400 w-24 shrink-0">
                                  {formatCalendarDate(e.startTime, tz, {
                                    style: "short",
                                    weekday: "short",
                                    instante: true,
                                  })}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/dashboard/eventos/${e.id}`)}
                                  className="min-w-0 flex-1 truncate text-left hover:underline"
                                  title={e.cliente ? `${e.nombre} · ${e.cliente}` : e.nombre}
                                >
                                  <span style={{ color: ACCENT }}>{e.nombre}</span>
                                  {e.cliente && (
                                    <span className="text-zinc-400"> · {e.cliente}</span>
                                  )}
                                </button>
                                <span className="w-28 text-right text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                                  {formatMXN(e.valor)}
                                </span>
                                <span className="w-28 text-right text-emerald-600 whitespace-nowrap">
                                  {e.cobrado > 0 ? formatMXN(e.cobrado) : "—"}
                                </span>
                                <span className="w-28 text-right text-amber-600 whitespace-nowrap">
                                  {e.saldo > 0 ? formatMXN(e.saldo) : "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="bg-zinc-50 dark:bg-zinc-800/40 border-t border-zinc-200 dark:border-zinc-700/60">
              <tr>
                <td className="px-4 py-2.5 font-semibold text-zinc-900 dark:text-white">Total por venir</td>
                <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white">{totales.eventos}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white whitespace-nowrap">{formatMXN(totales.valor)}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-emerald-600 whitespace-nowrap">{formatMXN(totales.cobrado)}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-amber-600 whitespace-nowrap">{formatMXN(totales.saldo)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="text-xs text-zinc-400">
        Cuenta los eventos de hoy en adelante que no están cancelados; el valor
        sale de la cotización cuando existe y del precio acordado cuando no. No
        hay utilidad aquí a propósito: los costos de un evento que todavía no
        ocurre no están capturados, y calcularla saldría de más. La utilidad
        aparece en el Estado de resultados el mes en que el evento pasa.
      </p>
    </div>
  );
};

export default ReporteAgendaVendida;
